import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { linkWithPopup } from "firebase/auth";
import { doc, updateDoc, deleteField, getDoc } from "firebase/firestore";
import { auth, provider, db } from "../firebase-config";
import {
  ANON_LINK_PROMPT_EVENT,
  markPromptDismissed,
  markPromptResolved,
} from "../services/anonLinkPrompt";
import { clearAnonCredentials } from "../services/anonCredentialBackup";
import { useToast } from "../context/ToastContext";

/**
 * AnonLinkPrompt
 *
 * Mounted once at the App level. Listens for the
 * `muuzbox:show-anon-link-prompt` CustomEvent (fired by
 * recordRequestAndMaybePrompt after the 2nd song request from an
 * anonymous user) and shows a slide-up modal directing the guest to
 * link their Google account so their request history survives across
 * sessions/devices.
 *
 * Uses linkWithPopup so the existing anonymous uid is preserved —
 * everything tied to that uid (request history, votes, chats) stays
 * attached after the upgrade.
 */
export default function AnonLinkPrompt() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [requestCount, setRequestCount] = useState(0);
  const { addToast } = useToast();

  useEffect(() => {
    const handler = async (e) => {
      // Verify the user is still a guest before showing. Two paths:
      //   (a) local Firebase Auth reports isAnonymous → definitely guest
      //   (b) local Firebase Auth reports NOT anonymous → could still
      //       be a guest bridged via signInWithCustomToken from another
      //       origin. Check Firestore users/{uid}.anonymousSince as the
      //       cross-origin source-of-truth.
      const user = auth?.currentUser;
      if (!user) return;
      if (!user.isAnonymous) {
        try {
          const snap = await getDoc(doc(db, "users", user.uid));
          if (!snap.exists() || !snap.data()?.anonymousSince) {
            // Firestore says they've already linked — don't nag.
            return;
          }
        } catch (err) {
          console.warn("[AnonLinkPrompt] Firestore check failed:", err?.message);
          return;
        }
      }
      setRequestCount(e?.detail?.requestCount || 0);
      setError("");
      setOpen(true);
    };
    window.addEventListener(ANON_LINK_PROMPT_EVENT, handler);
    return () => window.removeEventListener(ANON_LINK_PROMPT_EVENT, handler);
  }, []);

  const handleDismiss = useCallback(() => {
    markPromptDismissed();
    setOpen(false);
  }, []);

  const handleLinkGoogle = useCallback(async () => {
    if (!auth?.currentUser) return;
    setBusy(true);
    setError("");
    try {
      const result = await linkWithPopup(auth.currentUser, provider);
      // Success — same uid, now upgraded with Google credentials.
      const uid = result.user?.uid || auth.currentUser?.uid;
      if (uid) markPromptResolved(uid);
      // Clear the cross-origin anonymous marker on Firestore. Once
      // linkWithPopup succeeds, this user is no longer a guest on ANY
      // surface — the AnonLinkPrompt trigger checks this field to
      // suppress re-prompting on origins bridged via signInWithCustomToken.
      try {
        if (uid) {
          // ALSO stamp the newly-gained Google identity onto the users doc.
          // The doc was created while anonymous (no email), and without this
          // the account stays unfindable by email forever — venue managers
          // adding them as staff got "No account found for that email".
          const linkedEmail = (result.user?.email || auth.currentUser?.email || "").trim().toLowerCase();
          await updateDoc(doc(db, "users", uid), {
            anonymousSince: deleteField(),
            ...(linkedEmail ? { email: linkedEmail } : {}),
          });
        }
      } catch (e) {
        console.warn("[AnonLinkPrompt] anonymousSince clear failed:", e?.message);
      }
      // Wipe the multi-storage anon backup — this isn't an anonymous
      // device anymore, so no "previous anon" prompt should ever fire.
      try { clearAnonCredentials(); } catch (_) {}
      setOpen(false);
      // Celebration toast — first-name if we have it, otherwise generic.
      // Fires AFTER the modal has closed so the user sees the toast land
      // over the app, not stacked on the modal backdrop.
      try {
        const firstName =
          (result.user?.displayName || auth.currentUser?.displayName || "")
            .trim()
            .split(/\s+/)[0];
        const message = firstName
          ? `Welcome, ${firstName}! Your Muuzbox history is now saved 🎉`
          : "You're all set — your Muuzbox history is now saved 🎉";
        addToast(message);
      } catch (_) {}
    } catch (err) {
      const code = err?.code || "";
      // credential-already-in-use / email-already-in-use / etc. — the
      // Google account is already attached to a different muuzbox user.
      // We can't merge from the client side; we just inform the guest.
      if (code === "auth/credential-already-in-use" || code === "auth/email-already-in-use") {
        setError("That Google account is already linked to another Muuzbox profile.");
      } else if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        setError(""); // user cancelled — no error, just leave modal open
      } else if (code === "auth/popup-blocked") {
        setError("Pop-up blocked. Allow pop-ups for muuzbox.com and try again.");
      } else {
        setError(err?.message || "Couldn't link account. Try again in a moment.");
      }
    } finally {
      setBusy(false);
    }
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={handleDismiss}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.55)",
              zIndex: 9998,
            }}
          />
          {/* Bottom sheet */}
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "tween", duration: 0.28 }}
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              maxHeight: "85vh",
              background: "#121214",
              color: "#fff",
              borderTopLeftRadius: 18,
              borderTopRightRadius: 18,
              boxShadow: "0 -12px 32px rgba(0,0,0,0.5)",
              padding: "22px 22px 28px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div
              aria-hidden="true"
              style={{
                width: 38,
                height: 4,
                borderRadius: 4,
                background: "rgba(255,255,255,0.22)",
                alignSelf: "center",
                marginBottom: 4,
              }}
            />

            <div style={{ fontSize: "1.35rem", fontWeight: 700, lineHeight: 1.25 }}>
              Save your request history
            </div>

            <p style={{ fontSize: "0.95rem", opacity: 0.8, margin: 0, lineHeight: 1.5 }}>
              You've made{" "}
              {requestCount > 0 ? <strong>{requestCount}</strong> : "some"} requests so far.
              Link your Google account in one tap so your votes, chats and request history
              stay with you next time you scan into Muuzbox at any venue.
            </p>

            {error && (
              <div
                style={{
                  background: "rgba(255,80,80,0.12)",
                  border: "1px solid rgba(255,80,80,0.35)",
                  color: "#ffb4b4",
                  padding: "10px 12px",
                  borderRadius: 8,
                  fontSize: "0.85rem",
                }}
              >
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={handleLinkGoogle}
              disabled={busy}
              style={{
                marginTop: 6,
                background: "#fff",
                color: "#101010",
                border: "none",
                borderRadius: 10,
                padding: "13px 18px",
                fontWeight: 600,
                fontSize: "1rem",
                cursor: busy ? "wait" : "pointer",
                opacity: busy ? 0.7 : 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path
                  fill="#FFC107"
                  d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.5 29.3 4.5 24 4.5 12.7 4.5 3.5 13.7 3.5 25S12.7 45.5 24 45.5 44.5 36.3 44.5 25c0-1.6-.2-3.1-.9-4.5z"
                />
                <path
                  fill="#FF3D00"
                  d="M6.3 14.7l6.6 4.8C14.7 16 19 13.5 24 13.5c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.5 29.3 4.5 24 4.5 16.3 4.5 9.7 8.6 6.3 14.7z"
                />
                <path
                  fill="#4CAF50"
                  d="M24 45.5c5.2 0 9.8-1.9 13.3-5.1l-6.1-5c-2 1.5-4.6 2.5-7.2 2.5-5.2 0-9.6-3.3-11.3-8l-6.6 5.1C9.5 41.5 16.3 45.5 24 45.5z"
                />
                <path
                  fill="#1976D2"
                  d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.7l6.1 5c2.6-2.4 4.6-5.8 5.4-9.7.4-1.6.6-3.3.6-5 0-1.6-.2-3.1-.9-4.5z"
                />
              </svg>
              {busy ? "Linking…" : "Continue with Google"}
            </button>

            <button
              type="button"
              onClick={handleDismiss}
              disabled={busy}
              style={{
                background: "transparent",
                color: "#fff",
                border: "none",
                padding: "10px",
                fontSize: "0.9rem",
                opacity: 0.65,
                cursor: busy ? "wait" : "pointer",
              }}
            >
              Maybe later
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
